import { ChevronDown, Repeat } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router'
import { ChartCard } from '@/components/patterns/ChartCard'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { StatusBadge } from '@/components/ui/status-badge'
import { relationHref } from './links'
import { itemKey } from './itemKey'
import type { ItemActions } from './useItemActions'
import type { DashboardItem } from './types'

const lateText = (days: number | null): string => (days === 1 ? '1 dag te laat' : `${days ?? 0} dagen te laat`)

function OverdueRow({ item, today, actions }: { item: DashboardItem; today: string; actions: ItemActions }) {
  const [picking, setPicking] = useState(false)
  const [date, setDate] = useState('')
  const key = itemKey(item)
  const busy = actions.busy.has(key)
  const relationText = item.relation ? [item.relation.name, item.relation.context_name].filter(Boolean).join(' · ') : null
  const done = (
    <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => actions.complete(item)}>
      Afgehandeld
    </Button>
  )
  return (
    <li className="flex flex-col gap-2 py-3">
      <div className="flex items-start gap-2">
        {item.agreement_id ? <Repeat className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-label="Uit afspraak" role="img" /> : null}
        <span className="flex min-w-0 flex-1 flex-col">
          {item.relation ? (
            <Link to={relationHref(item.relation.type, item.relation.id)} className="truncate text-sm font-medium underline-offset-2 hover:underline">
              {item.title}
            </Link>
          ) : (
            <span className="truncate text-sm font-medium">{item.title}</span>
          )}
          <span className="truncate text-sm text-muted-foreground">{relationText}</span>
        </span>
        <StatusBadge tone="danger" calm>
          {lateText(item.days_late)}
        </StatusBadge>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {actions.rhythmAnchor(item, done)}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" size="sm" variant="outline" disabled={busy}>
              Uitstellen
              <ChevronDown aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onSelect={() => actions.postpone(item, { days: 7 })}>+1 week</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => actions.postpone(item, { days: 14 })}>+2 weken</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setPicking(true)}>Kies datum…</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {picking ? (
        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="date"
            aria-label={`Nieuwe datum voor ${item.title}`}
            min={today}
            value={date}
            onChange={(event) => setDate(event.target.value)}
            className="w-auto"
          />
          <Button
            type="button"
            size="sm"
            disabled={date === '' || busy}
            onClick={() => {
              actions.postpone(item, { date })
              setPicking(false)
            }}
          >
            Uitstellen tot datum
          </Button>
        </div>
      ) : null}
    </li>
  )
}

/** Widget 3: overdue tasks and deadlines, oldest first (design §4.1). */
export function OverdueWidget({ items, today, actions }: { items: DashboardItem[]; today: string; actions: ItemActions }) {
  return (
    <ChartCard
      title={`Achterstallig (${items.length})`}
      definition="Open taken en deadlines over datum, de oudste eerst"
      empty={items.length === 0 ? { title: 'Niets achterstallig.' } : null}
    >
      <ul className="divide-y divide-border">
        {items.map((item) => (
          <OverdueRow key={itemKey(item)} item={item} today={today} actions={actions} />
        ))}
      </ul>
    </ChartCard>
  )
}
